import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Bell, User, LogOut, Settings, FileText, Image, Film } from 'lucide-react'
import { useAuthStore } from '../../stores/authStore'
import { useProjectStore } from '../../stores/projectStore'
import { ModuleType } from '../../types'

interface HeaderProps {
  showModuleTabs?: boolean
}

const Header: React.FC<HeaderProps> = ({ showModuleTabs = false }) => {
  const [showUserMenu, setShowUserMenu] = useState(false)
  const { user, logout } = useAuthStore()
  const { currentModule, setCurrentModule, currentProject } = useProjectStore()

  const modules: { id: ModuleType; label: string; icon: React.ReactNode; color: string }[] = [
    { id: 'story', label: 'Story', icon: <FileText className="w-4 h-4" />, color: 'amber' },
    { id: 'images', label: 'Images', icon: <Image className="w-4 h-4" />, color: 'purple' },
    { id: 'video', label: 'Video', icon: <Film className="w-4 h-4" />, color: 'cyan' },
  ]

  const handleLogout = async () => {
    try {
      await logout()
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }

  const displayName = user?.displayName || user?.email?.split('@')[0] || 'User'

  return (
    <header className="bg-primary-800/50 backdrop-blur-md border-b border-glass-border">
      <div className="w-full px-6 py-3">
        <div className="flex items-center justify-between">
          {/* Logo and Title */}
          <div className="flex items-center space-x-3">
            <img src="/logotype_board.svg" alt="Board" className="h-[60px] w-auto" />
            
            {/* Module Tabs - Only show when viewing a project */}
            {showModuleTabs && currentProject && (
              <div className="flex items-center ml-6">
                <div className="glass-card p-1 flex space-x-1">
                  {modules.map((module) => {
                    const isActive = currentModule === module.id
                    const colorClasses = {
                      amber: isActive ? 'bg-amber-500/30 text-amber-300 ring-1 ring-amber-400/50' : 'text-white/60 hover:text-amber-300 hover:bg-amber-500/10',
                      purple: isActive ? 'bg-purple-500/30 text-purple-300 ring-1 ring-purple-400/50' : 'text-white/60 hover:text-purple-300 hover:bg-purple-500/10',
                      cyan: isActive ? 'bg-cyan-500/30 text-cyan-300 ring-1 ring-cyan-400/50' : 'text-white/60 hover:text-cyan-300 hover:bg-cyan-500/10',
                    }
                    
                    return (
                      <button
                        key={module.id}
                        onClick={() => setCurrentModule(module.id)}
                        className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200 font-ui text-sm ${colorClasses[module.color as keyof typeof colorClasses]}`}
                      >
                        {module.icon}
                        <span className="font-medium">{module.label}</span>
                      </button>
                    )
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Right Side */}
          <div className="flex items-center space-x-3">
            {/* Notifications */}
            <button className="glass-button p-2 hover:bg-glass-medium">
              <Bell className="w-5 h-5 text-white" />
            </button>

            {/* User Menu */}
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="glass-button p-2 hover:bg-glass-medium flex items-center space-x-2"
              >
                <div className="w-8 h-8 bg-primary-500/30 rounded-full flex items-center justify-center">
                  <User className="w-4 h-4 text-white" />
                </div>
                <span className="font-ui font-normal text-white">{displayName}</span>
              </button>

              <AnimatePresence>
                {showUserMenu && (
                  <motion.div
                    className="absolute right-0 top-full mt-2 w-48 glass-card border border-glass-border z-[99999]"
                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                    transition={{ duration: 0.15 }}
                  >
                    <div className="py-2">
                      <div className="px-4 py-2 border-b border-glass-border">
                        <p className="font-ui font-normal text-white truncate">{user?.email}</p>
                      </div>
                      
                      <button className="w-full px-4 py-2 text-left font-ui font-normal text-white hover:bg-glass-medium flex items-center space-x-2">
                        <Settings className="w-4 h-4" />
                        <span>Settings</span>
                      </button>
                      
                      <button
                        onClick={handleLogout}
                        className="w-full px-4 py-2 text-left font-ui font-normal text-red-300 hover:bg-red-500/20 flex items-center space-x-2"
                      >
                        <LogOut className="w-4 h-4" />
                        <span>Sign Out</span>
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>

      {/* Close menu when clicking outside */}
      {showUserMenu && (
        <div
          className="fixed inset-0 z-[99998]"
          onClick={() => setShowUserMenu(false)}
        />
      )}
    </header>
  )
}

export default Header 